<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { debounce } from "lodash";

import { useDefaultToast } from "@/composables/useDefaultToast";
import { useInfiniteScroll } from "@/composables/useInfiniteScroll";
import { useAuthStore } from "@/modules/auth/store/authStore";
import {
    applyBulkAddItemResult,
    buildAddWorkerTabSnapshot,
    buildAddWorkerTabsConfig,
    buildWorkersQueryParams,
    canFetchWorkersForTab,
    fetchWorkersForTab,
    formatWorkerAgenciesLabel,
    handleAddWorkerMissionError,
    isCachableAddWorkerTab,
    isServiceUnavailableError,
    mapWorkerToMissionBulkWorker,
    openTooMuchWorkerConfirmModal,
    resolveAddWorkerListStateText,
} from "@/modules/companyNeed/components/modals/mission/addWorkerToMission/helpers/addWorkerToMission";
import {
    ADD_WORKER_SEARCH_DEBOUNCE_MS,
    type AddWorkerTabCounts,
    AddWorkerTabId,
    type AddWorkerTabListSnapshot,
    type AddWorkerToMissionModalProps,
    ALL_PROFILES_MIN_SEARCH_LENGTH,
} from "@/modules/companyNeed/components/modals/mission/addWorkerToMission/types";
import missionService from "@/modules/companyNeed/services/missionService";
import { MissionBulkWorkerStatus } from "@/modules/companyNeed/types/missions";

import type { PoolWorkerAgency, WorkerPool } from "@/modules/companyNeed/views/details/pool/types";

import Loader from "@/dsComponents/Loader.vue";
import Modal from "@/dsComponents/modals/Modal.vue";
import { useModalStore } from "@/dsComponents/modals/modalStore";
import QuickActionBadge from "@/dsComponents/QuickActions/QuickActionBadge.vue";
import QuickActionSearch from "@/dsComponents/QuickActions/QuickActionSearch.vue";
import Tab from "@/dsComponents/Tab";

import PoolWorkersQuickActionItem from "@/modules/companyNeed/components/modals/mission/propositions/PoolWorkersQuickActionItem.vue";

const PAGE_LIMIT = 25;

const props = defineProps<AddWorkerToMissionModalProps>();

const emit = defineEmits<{
    close: [];
}>();

const { t } = useI18n();
const { toastSuccess, toastError } = useDefaultToast();
const modalStore = useModalStore();
const authStore = useAuthStore();

const search = ref("");
const addedIds = ref<Set<string>>(new Set());
const pendingIds = ref<Set<string>>(new Set());
const hasChanges = ref(false);
const activeTabId = ref<AddWorkerTabId>(AddWorkerTabId.POOL);
const searchInputFocusKey = ref(0);
const isServiceUnavailable = ref(false);
const tabCounts = ref<AddWorkerTabCounts>({});
const tabListCache = ref<Partial<Record<AddWorkerTabId, AddWorkerTabListSnapshot>>>({});
let skipNextSearchWatch = false;

const isPoolTab = computed(() => activeTabId.value === AddWorkerTabId.POOL);
const isAllProfilesTab = computed(() => activeTabId.value === AddWorkerTabId.ALL_PROFILES);
const trimmedSearch = computed(() => search.value.trim());
const excludedWorkerIdSet = computed(() => new Set(props.excludedWorkerIds));

const isAllProfilesSearchPromptVisible = computed(
    () => isAllProfilesTab.value && trimmedSearch.value.length < ALL_PROFILES_MIN_SEARCH_LENGTH
);

const canFetchWorkers = computed(() =>
    canFetchWorkersForTab({
        besoinId: props.besoinId,
        activeTabId: activeTabId.value,
        searchLength: trimmedSearch.value.length,
    })
);

const {
    items: workers,
    total: workersTotal,
    isLoading,
    isLoadingMore,
    hasNextPage,
    loadError,
    scrollRootEl,
    loadMoreTriggerEl,
    reload: reloadWorkers,
    getSnapshot,
    restoreSnapshot,
} = useInfiniteScroll<WorkerPool>({
    pageLimit: PAGE_LIMIT,
    initialLoading: true,
    getItemId: (worker) => worker.workerId,
    filterItem: (worker) => !excludedWorkerIdSet.value.has(worker.workerId),
    canObserve: () => !isAllProfilesSearchPromptVisible.value,
    fetchPage: async ({ offset, limit }) => {
        isServiceUnavailable.value = false;

        if (!canFetchWorkers.value) {
            return { items: [], total: 0 };
        }

        const response = await fetchWorkersForTab({
            besoinId: props.besoinId,
            activeTabId: activeTabId.value,
            queryParams: buildWorkersQueryParams({
                missionId: props.missionId,
                search: trimmedSearch.value,
                offset,
                limit,
            }),
            search: trimmedSearch.value,
        });

        return { items: response.items, total: response.total };
    },
    onError: (error) => {
        if (isServiceUnavailableError(error)) {
            isServiceUnavailable.value = true;
            return;
        }

        toastError(t("modals.missionDetails.propositions.addFromPool.addError"));
    },
});

const isListEmpty = computed(
    () =>
        !isLoading.value &&
        !loadError.value &&
        !isServiceUnavailable.value &&
        workers.value.length === 0
);

const isPoolEmpty = computed(
    () => isPoolTab.value && isListEmpty.value && trimmedSearch.value.length === 0 && workersTotal.value === 0
);
const isFilterEmpty = computed(() => isListEmpty.value && trimmedSearch.value.length > 0);

const listStateText = computed(() =>
    resolveAddWorkerListStateText({
        isAllProfilesSearchPromptVisible: isAllProfilesSearchPromptVisible.value,
        isLoading: isLoading.value,
        isServiceUnavailable: isServiceUnavailable.value,
        loadError: loadError.value,
        isListEmpty: isListEmpty.value,
        isFilterEmpty: isFilterEmpty.value,
        activeTabId: activeTabId.value,
        t,
    })
);

const isSearchDisabled = computed(() => isPoolEmpty.value);

const tabs = computed(() =>
    buildAddWorkerTabsConfig({
        activeTabId: activeTabId.value,
        t,
        tabCounts: tabCounts.value,
    })
);

const snapshotCurrentTabIfUnfiltered = () => {
    if (!isCachableAddWorkerTab(activeTabId.value)) {
        return;
    }

    if (trimmedSearch.value.length > 0 || isLoading.value || loadError.value || isServiceUnavailable.value) {
        return;
    }

    tabListCache.value[activeTabId.value] = getSnapshot();
};

const updateActiveTabCount = () => {
    if (!isCachableAddWorkerTab(activeTabId.value)) {
        return;
    }

    if (isLoading.value || loadError.value || trimmedSearch.value.length > 0) {
        return;
    }

    tabCounts.value = {
        ...tabCounts.value,
        [activeTabId.value]: workersTotal.value,
    };
};

const prefetchOngoingProposalTab = async () => {
    if (!props.besoinId) {
        return;
    }

    try {
        const response = await fetchWorkersForTab({
            besoinId: props.besoinId,
            activeTabId: AddWorkerTabId.ONGOING_PROPOSAL,
            queryParams: buildWorkersQueryParams({
                missionId: props.missionId,
                search: "",
                offset: 0,
                limit: PAGE_LIMIT,
            }),
            search: "",
        });

        if (!tabListCache.value[AddWorkerTabId.ONGOING_PROPOSAL]) {
            tabListCache.value[AddWorkerTabId.ONGOING_PROPOSAL] = buildAddWorkerTabSnapshot({
                items: response.items,
                total: response.total,
                pageLimit: PAGE_LIMIT,
                excludedWorkerIds: props.excludedWorkerIds,
            });
        }

        tabCounts.value = {
            ...tabCounts.value,
            [AddWorkerTabId.ONGOING_PROPOSAL]: response.total,
        };
    } catch {
        tabCounts.value = {
            ...tabCounts.value,
            [AddWorkerTabId.ONGOING_PROPOSAL]: undefined,
        };
    }
};

const showTabList = (tabId: AddWorkerTabId) => {
    const cachedList = tabListCache.value[tabId];

    if (cachedList) {
        isServiceUnavailable.value = false;
        restoreSnapshot(cachedList);
        return;
    }

    void reloadWorkers();
};

const debouncedReloadWorkers = debounce(() => {
    void reloadWorkers();
}, ADD_WORKER_SEARCH_DEBOUNCE_MS);

const assignScrollRootEl = (element: unknown) => {
    scrollRootEl.value = element instanceof HTMLElement ? element : null;
};

const assignLoadMoreTriggerEl = (element: unknown) => {
    loadMoreTriggerEl.value = element instanceof HTMLElement ? element : null;
};

const handleSelectTab = (selectedTab: AddWorkerTabId) => {
    if (activeTabId.value === selectedTab) {
        return;
    }

    debouncedReloadWorkers.cancel();
    snapshotCurrentTabIfUnfiltered();

    if (search.value.length > 0) {
        skipNextSearchWatch = true;
    }

    activeTabId.value = selectedTab;
    search.value = "";

    if (selectedTab === AddWorkerTabId.ALL_PROFILES) {
        searchInputFocusKey.value += 1;
    }

    showTabList(selectedTab);
};

const setWorkerPending = (workerId: string, pending: boolean) => {
    const next = new Set(pendingIds.value);

    if (pending) {
        next.add(workerId);
    } else {
        next.delete(workerId);
    }

    pendingIds.value = next;
};

const markWorkerAdded = (workerId: string) => {
    addedIds.value = new Set(addedIds.value).add(workerId);
    hasChanges.value = true;
};

const isPending = (workerId: string) => pendingIds.value.has(workerId);

const isAdded = (workerId: string) => addedIds.value.has(workerId);

const availableAgencies = computed(() => {
    const agencies = [...authStore.supervisorAgencies];

    if (authStore.currentAgency?.id) {
        agencies.push(authStore.currentAgency);
    }

    return agencies;
});

const resolveWorkerAgencies = (agencies?: PoolWorkerAgency[]): PoolWorkerAgency[] | undefined => {
    if (!agencies?.length) {
        return agencies;
    }

    return agencies.map((agency) => {
        if (agency.publicName || agency.agencyName) {
            return agency;
        }

        const matchedAgency = availableAgencies.value.find((candidate) => String(candidate.id) === String(agency.agencyId));

        if (!matchedAgency) {
            return agency;
        }

        return {
            agencyId: agency.agencyId,
            agencyName: matchedAgency.name ?? null,
            publicName: matchedAgency.publicName ?? null,
        };
    });
};

const getWorkerAgencyLabel = (worker: WorkerPool) => {
    if (!isAllProfilesTab.value) {
        return null;
    }

    return formatWorkerAgenciesLabel({
        agencies: resolveWorkerAgencies(worker.agencies),
        agencyName: worker.agencyName,
        currentAgencyId: authStore.currentAgency?.id ?? null,
        t,
    });
};

const handleAdd = async (worker: WorkerPool) => {
    const workerId = worker.workerId;

    if (pendingIds.value.has(workerId) || addedIds.value.has(workerId)) {
        return;
    }

    setWorkerPending(workerId, true);

    const bulkWorker = mapWorkerToMissionBulkWorker(worker);

    try {
        const result = await missionService.addWorkersToMissionBulk(props.missionId, {
            status: MissionBulkWorkerStatus.Proposed,
            workers: [bulkWorker],
        });
        const item = result.find((resultItem) => resultItem.workerId === workerId);
        applyBulkAddItemResult(item, {
            onSuccess: () => {
                markWorkerAdded(workerId);
                props.onWorkerAdded?.(bulkWorker, { bumpMissionsListQuantity: true });
                toastSuccess(t("modals.missionDetails.propositions.addFromPool.addSuccess"));
            },
            onAlreadyAssigned: () => {
                markWorkerAdded(workerId);
                props.onWorkerAdded?.(bulkWorker, { bumpMissionsListQuantity: false });
            },
            onMaxWorkersReached: () => toastError(t("modals.missionDetails.propositions.addFromPool.maxReachedError")),
            onRegistrationNotComplete: () =>
                toastError(t("modals.missionDetails.propositions.addFromPool.registrationNotCompleteError")),
            onError: () => toastError(t("modals.missionDetails.propositions.addFromPool.addError")),
        });
    } catch (error) {
        handleAddWorkerMissionError(error, {
            onMaxWorkersReached: () => openTooMuchWorkerConfirmModal((modalName) => modalStore.actions.open(modalName)),
            onError: () => toastError(t("modals.missionDetails.propositions.addFromPool.addError")),
        });
    } finally {
        setWorkerPending(workerId, false);
    }
};

const handleClose = () => {
    if (hasChanges.value) {
        props.onAllAdditionsDone?.();
    }

    emit("close");
};

const handleClearSearch = () => {
    search.value = "";
};

watch(
    [() => props.besoinId, () => props.missionId],
    () => {
        tabListCache.value = {};
        tabCounts.value = {};
        void reloadWorkers();
        void prefetchOngoingProposalTab();
    },
    { immediate: true }
);

watch([isLoading, loadError, workersTotal], () => {
    snapshotCurrentTabIfUnfiltered();
    updateActiveTabCount();
});

watch(trimmedSearch, (searchValue, previousSearch) => {
    if (skipNextSearchWatch) {
        skipNextSearchWatch = false;
        return;
    }

    if (
        isCachableAddWorkerTab(activeTabId.value) &&
        !previousSearch &&
        searchValue.length > 0 &&
        !isLoading.value &&
        !loadError.value &&
        !isServiceUnavailable.value
    ) {
        tabListCache.value[activeTabId.value] = getSnapshot();
    }

    if (isAllProfilesSearchPromptVisible.value) {
        void reloadWorkers();
        return;
    }

    if (searchValue.length === 0) {
        const cachedList = tabListCache.value[activeTabId.value];

        if (cachedList) {
            isServiceUnavailable.value = false;
            restoreSnapshot(cachedList);
            return;
        }
    }

    debouncedReloadWorkers();
});
</script>

<template>
    <Modal class="add-from-pool-modal" position="top" width="720px" :open="open" no-padding no-padding-header no-padding-footer @close="handleClose">
        <template #header>
            <div class="add-from-pool-modal__header">
                <QuickActionBadge :label="missionTitle" />
                <QuickActionSearch
                    :key="`${activeTabId}-${searchInputFocusKey}`"
                    v-model="search"
                    :is-focused="open && (isAllProfilesTab || !isSearchDisabled)"
                    :disabled="isSearchDisabled"
                    :placeholder="t('modals.missionDetails.propositions.addWorkerToMission.searchPlaceholder')"
                    icon="search"
                    @clear="handleClearSearch"
                />
            </div>
        </template>
        <template #default>
            <div class="add-from-pool-modal__tabs">
                <Tab v-bind="tabs" @select:tab="handleSelectTab" />
            </div>

            <div class="add-from-pool-modal__body">
                <div v-if="isAllProfilesSearchPromptVisible" class="add-from-pool-modal__all-profiles-prompt">
                    <i class="ti ti-search add-from-pool-modal__all-profiles-prompt-icon" />
                    <div class="add-from-pool-modal__all-profiles-prompt-text">
                        <p class="typo-sm-medium">
                            {{ t("modals.missionDetails.propositions.addWorkerToMission.tabs.allProfiles") }}
                        </p>
                        <p class="add-from-pool-modal__all-profiles-prompt-description typo-sm-regular">
                            {{ t("modals.missionDetails.propositions.addWorkerToMission.allProfilesSearchPrompt") }}
                        </p>
                    </div>
                </div>
                <div v-else :ref="assignScrollRootEl" class="add-from-pool-modal__scroll">
                    <div v-if="listStateText !== null" class="add-from-pool-modal__state typo-sm-regular">
                        {{ listStateText }}
                    </div>
                    <div v-else class="add-from-pool-modal__list">
                        <PoolWorkersQuickActionItem
                            v-for="worker in workers"
                            :key="worker.workerId"
                            :worker="worker"
                            :agency-label="getWorkerAgencyLabel(worker)"
                            :is-pending="isPending(worker.workerId)"
                            :is-added="isAdded(worker.workerId)"
                            @add="() => handleAdd(worker)"
                        />
                        <div
                            v-if="hasNextPage"
                            :ref="assignLoadMoreTriggerEl"
                            class="add-from-pool-modal__load-more-trigger"
                            aria-hidden="true"
                        />
                        <div v-if="isLoadingMore" class="add-from-pool-modal__loading-more">
                            <Loader size="sm" />
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </Modal>
</template>

<style scoped lang="scss">
.add-from-pool-modal {
    &__header {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-md, 8px);
        box-sizing: border-box;
        min-width: 0;
        width: 100%;
        padding: var(--spacing-md, 8px) var(--spacing-lg, 12px);
    }

    &__body {
        padding: 0 var(--spacing-md, 8px);
    }

    &__scroll {
        display: flex;
        max-height: 600px;
        flex-direction: column;
        padding: var(--spacing-xs, 4px) var(--spacing-md, 8px) var(--spacing-md, 8px) var(--spacing-xxs, 2px);
        overflow-y: auto;
        -ms-overflow-style: none;
        scrollbar-width: none;

        &::-webkit-scrollbar {
            display: none;
        }
    }

    &__list {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-xxs, 2px);
    }

    &__state {
        padding: var(--spacing-4xl, 32px) var(--spacing-xl, 16px);
        text-align: center;
        color: var(--text-sub-600, #494e61);
    }

    &__all-profiles-prompt {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: var(--spacing-xl, 16px);
        padding: var(--spacing-4xl, 32px) 0;
    }

    &__all-profiles-prompt-icon {
        font-size: 16px;
        line-height: 1;
        color: var(--text-main-900, #00061a);
    }

    &__all-profiles-prompt-text {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--spacing-md, 8px);
        text-align: center;

        p {
            margin: 0;
        }
    }

    &__all-profiles-prompt-description {
        max-width: 328px;
        color: var(--text-sub-600, #494e61);
    }

    &__load-more-trigger {
        width: 100%;
        height: 1px;
        flex-shrink: 0;
    }

    &__loading-more {
        display: flex;
        justify-content: center;
        padding: var(--spacing-md, 8px) 0;
    }
}
</style>
